// Shared resource/blog content. Import from here in both preview + full pages.

export const featured = {
 slug: "first-time-founders-complete-map",
 tag: "Guide",
 title:
 "The complete first-time founder's map: structure, registration, compliance and launch",
 seoTitle: "The First-Time Founder's Map: Start to Launch",
 seoDescription:
 "The complete map for Indian founders: structure, registration, DPIIT recognition, compliance and launch — in the order it matters.",
 read: "12 min read",
 img: "https://images.unsplash.com/photo-1553877522-43269d4ea984?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
 excerpt:
 "The one guide we wish existed when we started every decision a first-time founder in India has to make, in the order it matters.",
 sections: [
 {
 h: "Why most founders stumble in the first 90 days",
 p: [
 "In India, the biggest cost of a wrong start isn't the ₹999 you spent on a filing it's the six months you'll spend fixing it. Wrong structure, missing agreements, and forgotten compliances quietly erode credibility with clients, banks and investors.",
 "This guide gives you the exact sequence a first-time founder should follow from structure decision to first invoice.",
 ],
 },
 {
 h: "Step 1 Choose the right structure",
 p: [
 "Pvt Ltd suits founders planning to raise capital or hire teams. LLP works for consulting and agency firms. OPC lets solo founders start with limited liability. Section 8 is for non-profits.",
 "Skip the tempting ₹999 packages that pick structure for you. A 15-minute conversation before payment saves months of pain later.",
 ],
 },
 {
 h: "Step 2 Get your registration done right",
 p: [
 "Get DIN + DSC for directors, reserve your name (RUN or SPICe+), file MOA/AOA, and receive the Certificate of Incorporation. PAN, TAN and bank-account letter follow automatically.",
 "Ask your provider for a copy of every document a lot of first-time founders discover months later that MOA objects were copy-pasted from an unrelated business.",
 ],
 },
 {
 h: "Step 3 Unlock startup benefits",
 p: [
 "DPIIT recognition under Startup India opens up 80-IAC income-tax exemption, angel-tax waiver, IPR fast-tracking and public-tender eligibility. Apply as soon as you're incorporated.",
 "Add MSME (Udyam) registration for procurement priority and delayed-payment protection free, and takes 10 minutes.",
 ],
 },
 {
 h: "Step 4 Build compliance rails from day one",
 p: [
 "Set up a compliance calendar covering ROC annual filings (AOC-4, MGT-7), income tax, GST returns (if registered) and TDS. Missing any of these snowballs into penalties that hurt future fundraising.",
 ],
 },
 {
 h: "Step 5 Launch a credible presence",
 p: [
 "Domain, professional Google Workspace email, a one-page website and a Google Business profile this is the credibility floor. Do it in the first 30 days, not after your first customer complaint.",
 ],
 },
 {
 h: "One last thing",
 p: [
 "The right partner in year 1 isn't the cheapest it's the one who picks up the phone in month 7. Pick accordingly.",
 ],
 },
 ],
};

export const posts = [
 {
 slug: "pvt-ltd-vs-llp-vs-opc-2026",
 tag: "Structure",
 title: "PVT. LTD. vs LLP vs OPC in 2026 a decision guide",
 seoTitle: "Pvt Ltd vs LLP vs OPC: 2026 Decision Guide",
 seoDescription:
 "The decision that shapes tax, cap-table, hiring and fundraising for the next 5 years. A plain-English comparison for Indian founders.",
 read: "6 min",
 img: "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "The single decision that shapes your tax, cap-table, hiring and fundraising for the next five years. Made simple.",
 sections: [
 {
 h: "The lens: what are you optimising for?",
 p: [
 "Raising capital in 12–24 months → almost always PVT. LTD. Investors don't do LLPs.",
 "Bootstrapped consulting / agency with 1–5 partners → LLP is lean and cheaper to run.",
 "Solo founder testing an idea → OPC gives limited liability without needing a co-founder.",
 ],
 },
 {
 h: "PVT. LTD. pros and cons",
 p: [
 "Pros: perceived credibility, ESOP-ready, easiest to raise money, clean cap-table.",
 "Cons: higher compliance (ROC, board meetings, statutory audits), higher setup cost.",
 ],
 },
 {
 h: "LLP pros and cons",
 p: [
 "Pros: lean compliance, pass-through-ish taxation, flexible partner economics.",
 "Cons: cannot issue equity, VCs will ask you to convert before they invest.",
 ],
 },
 {
 h: "OPC pros and cons",
 p: [
 "Pros: solo founder gets limited liability + corporate identity.",
 "Cons: must convert to PVT. LTD. within 2 years if turnover > ₹2Cr or paid-up capital > ₹50L.",
 ],
 },
 {
 h: "Bhavik's advice",
 p: [
 "If you're unsure take a 15-minute call before you register. Wrong structure costs 3–4× more to fix than to do right the first time.",
 ],
 },
 ],
 },
 {
 slug: "dpiit-recognition-founder-checklist",
 tag: "DPIIT",
 title: "How to get DPIIT recognition the founder-friendly checklist",
 seoTitle: "DPIIT Recognition: Founder-Friendly Checklist",
 seoDescription:
 "Everything you need to file for DPIIT recognition, including the innovation writeup that actually gets approved for Startup India.",
 read: "8 min",
 img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "Everything you need to file for Startup India / DPIIT including the innovation writeup that actually gets approved.",
 sections: [
 {
 h: "Who qualifies",
 p: [
 "Entity must be a PVT. LTD., LLP or Partnership less than 10 years old and turnover under ₹100 Cr in any prior FY.",
 "The business must work toward innovation, development or improvement of products / services / processes or have a scalable business model with high job-creation potential.",
 ],
 },
 {
 h: "What you'll need",
 p: [
 "Certificate of Incorporation, PAN, founder details, website or pitch deck, a short innovation summary (200–500 words), and a scalability writeup.",
 ],
 },
 {
 h: "The writeup that actually gets approved",
 p: [
 "Explain the problem you solve, why it's a problem worth solving, your product / process, and 2–3 measurable proof-points (users, revenue, LOIs, IP).",
 "Avoid vague words like 'disruptive' and 'revolutionary'. Concrete beats poetic every time.",
 ],
 },
 {
 h: "What you unlock",
 p: [
 "80-IAC income tax holiday (subject to approval), angel-tax exemption under Section 56(2)(viib), IPR fast-tracking with 80% patent-fee rebate, and access to government procurement portals.",
 ],
 },
 ],
 },
 {
 slug: "first-90-days-after-incorporation",
 tag: "Compliance",
 title: "Your first 90 days after incorporation",
 seoTitle: "First 90 Days After Company Incorporation",
 seoDescription:
 "A 90-day sprint that separates compliant founders from those scrambling in month 8. Fortnightly steps for Indian startups after incorporation.",
 read: "5 min",
 img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "A no-nonsense 90-day sprint that quietly separates the founders who stay compliant from those who scramble in month 8.",
 sections: [
 {
 h: "Days 0–15",
 p: [
 "Open the current account (use the COI, PAN, MOA, board resolution). Deposit share subscription money (Pvt Ltd) or partner capital (LLP).",
 "Get a professional email domain and Google Business profile live.",
 ],
 },
 {
 h: "Days 15–45",
 p: [
 "File INC-20A (declaration of business commencement) within 180 days but do it in the first 45 to keep momentum.",
 "Register for GST if your projected turnover crosses the threshold OR if you're B2B (input tax credit matters).",
 "Register on Udyam (MSME) free and takes 10 minutes.",
 ],
 },
 {
 h: "Days 45–90",
 p: [
 "First board meeting + minutes signed. Founders' agreement and vesting schedule finalised.",
 "Apply for DPIIT recognition (see the dedicated guide). Set up an accounting tool (Zoho Books, Tally or QuickBooks) and start invoicing.",
 ],
 },
 ],
 },
 {
 slug: "cap-table-basics-first-time-founders",
 tag: "Funding",
 title: "Cap-table basics every first-time founder should know",
 seoTitle: "Cap Table Basics for First-Time Founders",
 seoDescription:
 "Five ideas every first-time founder must understand before signing a term sheet: authorised capital, vesting, ESOP pool, and more.",
 read: "7 min",
 img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "You don't need to be a CFO but you must understand these five ideas before you sign your first term sheet.",
 sections: [
 {
 h: "1. Authorised vs paid-up capital",
 p: [
 "Authorised is the ceiling. Paid-up is what's actually issued. Start with a reasonable authorised (e.g., ₹10L) so you don't need MCA amendments every round.",
 ],
 },
 {
 h: "2. Founder vesting",
 p: [
 "Even if there's just you and a co-founder set up a 4-year vesting with a 1-year cliff. It protects the surviving founder if one leaves early.",
 ],
 },
 {
 h: "3. ESOP pool",
 p: [
 "Reserve 8–12% for employees before your seed round. VCs almost always insist on it, and it's better to dilute yourself pre-round than watch your stake shrink mid-negotiation.",
 ],
 },
 {
 h: "4. Convertible instruments",
 p: [
 "Understand CCPS and SAFE-equivalent instruments. Get comfortable with valuation cap and discount before your first investor call.",
 ],
 },
 {
 h: "5. Keep it updated",
 p: [
 "Every share issuance, ESOP grant and transfer updates your cap table. A messy cap table has killed more good deals than bad ideas.",
 ],
 },
 ],
 },
 {
 slug: "gst-for-early-stage-startups",
 tag: "GST",
 title: "GST for early-stage startups what to file, what to skip",
 seoTitle: "GST for Early-Stage Startups: What to File",
 seoDescription:
 "When GST registration is mandatory, when to register voluntarily, what to file each month, and the mistakes that cost early-stage startups.",
 read: "6 min",
 img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "You don't have to register on day one but the moment you should is easy to miss. Here's the checklist.",
 sections: [
 {
 h: "When registration is mandatory",
 p: [
 "Turnover over ₹40 lakh (₹20 lakh for services) in a financial year, or you sell inter-state, or you sell via e-commerce platforms register immediately.",
 ],
 },
 {
 h: "When you should register voluntarily",
 p: [
 "If you're B2B and your clients want input tax credit, register early. Otherwise you'll leave money on the table by pricing without GST.",
 ],
 },
 {
 h: "What you must file after registration",
 p: [
 "GSTR-1 (outward supplies) and GSTR-3B (summary + payment) monthly or quarterly depending on the QRMP scheme you opt for. Annual return GSTR-9 if turnover > ₹2 Cr.",
 ],
 },
 {
 h: "The mistakes that cost most",
 p: [
 "Missing invoice matching, wrong HSN codes, and not claiming input tax credit within the deadline. These are boring to fix but expensive to ignore.",
 ],
 },
 ],
 },
 {
 slug: "minimum-credible-online-presence",
 tag: "Launch",
 title: "Domain, email, website the minimum credible presence",
 seoTitle: "Domain, Email & Website: The Credibility Floor",
 seoDescription:
 "The four assets that unlock everything else: a branded domain, professional email, a one-page website and a Google Business Profile.",
 read: "4 min",
 img: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
 excerpt:
 "Before you pitch a single customer, invest one afternoon in these four assets. Everything else in your business gets easier.",
 sections: [
 {
 h: "1. A branded domain",
 p: [
 ".com is still the default. If unavailable, .co, .in and .io work fine but avoid free subdomains. Pay the ₹800/year.",
 ],
 },
 {
 h: "2. Professional email",
 p: [
 "you@yourdomain.com beats yourbrand@gmail.com by a mile in perceived credibility. Google Workspace at ₹136/month/user is the fastest path.",
 ],
 },
 {
 h: "3. A one-page website",
 p: [
 "Value prop, three service lines, testimonials, contact CTA. That's it. Ship it in a weekend. Iterate later.",
 ],
 },
 {
 h: "4. Google Business Profile",
 p: [
 "Free. Local SEO, review collection, phone-through-Google all in one. Do this even if you're a purely-online business.",
 ],
 },
 ],
 },
 {
 slug: "things-to-know-before-registering-pvt-ltd",
 tag: "Guide",
 title: "Things to know before registering a Pvt Ltd company in India",
 seoTitle: "Things to Know Before Registering a Pvt Ltd Company",
 seoDescription:
 "Before you pay ₹999 for a Pvt Ltd, understand the responsibilities, compliance and long-term costs. A founder's honest guide.",
 read: "15 min",
 img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
 excerpt:
 "You should not register a Pvt Ltd just because someone is offering it at ₹999 or ₹9,999. Incorporation is the beginning of the company's legal and compliance journey — not the end of it. Here is what a founder should understand first.",
 sections: [
 {
 h: "1. First understand what you are registering",
 p: [
 "A Private Limited Company is a separate legal entity incorporated under the Companies Act, 2013.",
 "Once incorporated, the company may have its own:",
 {
 list: [
 "Name",
 "CIN",
 "PAN",
 "TAN",
 "Bank account",
 "Share capital",
 "Assets",
 "Liabilities",
 "Contracts",
 "Revenue",
 "Expenses",
 "Employees",
 "Tax obligations",
 "Statutory records",
 "Compliance responsibilities",
 ],
 },
 "The company is separate from its shareholders and directors.",
 "Therefore, before incorporation, a founder should understand what it means to create and maintain a separate legal entity.",
 ],
 },
 {
 h: "2. Do not register a Pvt Ltd simply because someone is offering it cheaply",
 p: [
 "You may come across offers such as:",
 {
 list: [
 "Pvt Ltd Company Registration ₹999",
 "Company Registration ₹4,999",
 "Complete Registration ₹9,999",
 ],
 },
 "The price itself should not determine whether you should incorporate a company. The more important question is:",
 { quote: "Why do you need a Private Limited Company?" },
 "And after that:",
 { quote: "What responsibilities will start once the company is incorporated?" },
 "A company may continue to have statutory and tax obligations even when the business has very little activity or no revenue. Therefore, the decision to incorporate should be based on the business requirement and long-term plans — not merely on a promotional price.",
 ],
 },
 {
 h: "3. Registration is only the beginning",
 p: [
 "Many first-time entrepreneurs think:",
 { quote: "Name Approved → Company Incorporated → Work Finished" },
 "That is not how a company works. A more realistic journey is:",
 {
 flow: [
 "Decide business structure",
 "Incorporate company",
 "Open bank account",
 "Bring in share capital",
 "Complete applicable post-incorporation requirements",
 "Start business operations",
 "Maintain books & records",
 "GST / Income Tax / TDS / Payroll / other applicable compliance",
 "Annual company compliance",
 ],
 },
 "The exact requirements will depend upon the company's activities, location, turnover, employees and other circumstances.",
 ],
 },
 {
 h: "4. Companies Act, 2013",
 p: [
 "The Companies Act, 2013 is one of the principal laws governing companies in India. A Private Limited Company does not become free from the Companies Act after incorporation. It continues to have obligations under the Act and applicable rules.",
 "Depending upon the circumstances, these may include requirements relating to:",
 {
 list: [
 "Board meetings",
 "General meetings",
 "Minutes",
 "Financial statements",
 "Annual return",
 "Statutory registers",
 "Directors",
 "Shareholders",
 "Share capital",
 "Auditor",
 "Registered office",
 "Statutory filings",
 "Changes in directors, registered office, share capital or other corporate actions",
 ],
 },
 "The exact compliance requirements depend upon the company and the applicable provisions.",
 ],
 },
 {
 h: "5. Directors have responsibilities",
 p: [
 "A director is not merely a person whose name appears in the incorporation documents. Directors have responsibilities under company law and may also have responsibilities under other applicable laws.",
 "Directors should understand the company's:",
 {
 list: [
 "Financial position",
 "Statutory obligations",
 "Tax obligations",
 "Corporate records",
 "Major transactions",
 "Compliance status",
 "Government communications",
 ],
 },
 "A person should therefore not agree to become a director simply to fulfil a registration requirement without understanding the responsibilities involved.",
 ],
 },
 {
 h: "6. Shareholders and directors are not the same",
 p: [
 "A shareholder is an owner or member of the company. A director is responsible for participating in the management and governance of the company. One person can be both a shareholder and a director, but the concepts are different.",
 "Before incorporation, founders should understand:",
 {
 list: [
 "Who will own the company?",
 "Who will be the directors?",
 "What percentage will each shareholder own?",
 "How many shares will be issued?",
 ],
 },
 "These decisions can become important when the business grows, new investors enter or ownership changes.",
 ],
 },
 {
 h: "7. Understand authorised and paid-up share capital",
 p: [
 "Two terms frequently encountered during incorporation are:",
 { h3: "Authorised share capital" },
 "The maximum share capital for which the company is authorised to issue shares, subject to the applicable legal framework.",
 { h3: "Paid-up share capital" },
 "The amount of share capital actually paid up on the shares issued to shareholders.",
 "Founders should understand the shareholding structure rather than simply accepting numbers suggested during incorporation.",
 ],
 },
 {
 h: "8. The company will need proper books and records",
 p: [
 "A company should maintain appropriate accounting records and supporting documents. This includes records relating to:",
 {
 list: [
 "Sales",
 "Purchases",
 "Expenses",
 "Bank transactions",
 "Receivables and payables",
 "Loans",
 "Investments",
 "Assets",
 "Salaries",
 "Taxes",
 "Other business transactions",
 ],
 },
 "Good accounting is not only useful for tax purposes. It helps the directors understand the actual financial position of the company.",
 ],
 },
 {
 h: "9. Income tax",
 p: [
 "A company is a separate taxpayer. The company may have obligations under the applicable Income-tax law depending upon its activities and circumstances. These can include:",
 {
 list: [
 "Income-tax return",
 "Advance tax",
 "Tax audit, where applicable",
 "TDS and TDS returns",
 "Tax payments",
 "Maintenance of records",
 "Responding to notices",
 ],
 },
 "A company does not become exempt from income-tax compliance simply because it is newly incorporated or has limited business activity.",
 ],
 },
 {
 h: "10. GST",
 p: [
 "GST is another important area that founders should understand. GST registration is not automatically required merely because a company has been incorporated. Applicability depends upon factors such as:",
 {
 list: [
 "Nature of business",
 "Turnover",
 "Location",
 "Type of supply",
 "Interstate transactions",
 "E-commerce activities",
 "Other applicable provisions",
 ],
 },
 "Once registered, the business may have continuing GST responsibilities such as:",
 {
 list: [
 "GST returns",
 "Tax payment",
 "Invoicing",
 "Input Tax Credit documentation",
 "E-invoicing, where applicable",
 "E-way bill requirements, where applicable",
 "Record maintenance",
 ],
 },
 { quote: "Company registration and GST registration are two different things." },
 ],
 },
 {
 h: "11. TDS",
 p: [
 "A Private Limited Company may become responsible for deducting tax at source on specified payments when the applicable conditions are satisfied.",
 "Depending upon the nature of transactions, TDS provisions can apply to payments such as:",
 {
 list: [
 "Salary",
 "Professional fees",
 "Contractor payments",
 "Rent",
 "Interest",
 "Commission",
 ],
 },
 "Where applicable, the company may need to deduct, deposit, report, issue applicable certificates and maintain records. TDS should therefore be considered as part of ongoing financial compliance.",
 ],
 },
 {
 h: "12. Professional Tax (PT)",
 p: [
 "Professional Tax is primarily a State-level matter and the applicable requirements vary from State to State. For example, businesses operating in Maharashtra should examine the applicable Maharashtra Professional Tax provisions.",
 "Depending upon the circumstances, requirements may include registration, enrolment, deduction, payment, returns and records.",
 "A founder should therefore understand that incorporation does not automatically complete every State-level registration applicable to the business.",
 ],
 },
 {
 h: "13. Provident Fund (PF / EPF)",
 p: [
 "When a company employs people, employment-related laws can become relevant. The Employees' Provident Fund (EPF) framework may apply when the applicable statutory conditions are satisfied.",
 "Depending upon applicability, employers may have responsibilities relating to:",
 {
 list: [
 "Employer contribution",
 "Employee contribution",
 "Payroll",
 "Monthly payments",
 "Employee records",
 "Returns and other compliance",
 ],
 },
 "The applicability should be determined based on the company's actual circumstances and the applicable law.",
 ],
 },
 {
 h: "14. Employees' State Insurance (ESI)",
 p: [
 "ESI may also become applicable to an establishment depending upon factors such as:",
 {
 list: [
 "Employee strength",
 "Employee wages",
 "Nature of establishment",
 "Location",
 "Applicable statutory conditions",
 ],
 },
 "Where applicable, the employer may have responsibilities relating to registration, contributions, records and periodic compliance.",
 ],
 },
 {
 h: "15. Employees mean additional compliance",
 p: [
 "Hiring employees is not simply a matter of transferring salary every month. Depending upon the circumstances, an employer may need to consider:",
 {
 list: [
 "Appointment documentation",
 "Payroll",
 "Salary records",
 "TDS",
 "Professional Tax",
 "PF",
 "ESI",
 "Leave",
 "Applicable labour laws",
 "Shops and Establishments requirements",
 "Minimum wage requirements",
 "Other employment-related requirements",
 ],
 },
 "The exact requirements vary according to the State, establishment and workforce.",
 ],
 },
 {
 h: "16. Your company's bank account is not your personal bank account",
 p: [
 "Once a company is incorporated, its financial transactions should be properly separated from the personal transactions of its founders. For example:",
 {
 list: [
 "Customer payment → Company",
 "Business expense → Company",
 "Founder investment → Properly recorded",
 "Founder reimbursement → Properly documented",
 ],
 },
 "The company's money should not simply be treated as the personal money of the shareholders or directors. Proper accounting and documentation are essential.",
 ],
 },
 {
 h: "17. Incorporation does not automatically give permission to conduct every business",
 p: [
 "A company incorporated under the Companies Act may still require other registrations, licences or approvals depending upon its business. Additional requirements may arise for businesses involved in:",
 {
 list: [
 "Food",
 "Import / export",
 "Manufacturing",
 "Healthcare",
 "Education",
 "Financial services",
 "E-commerce",
 "Employment-intensive activities",
 "Environment-sensitive activities",
 "Regulated sectors",
 ],
 },
 { quote: "Company registration ≠ business licence" },
 "Incorporation creates the company. It does not automatically authorise every possible business activity.",
 ],
 },
 {
 h: "18. Statutory audit",
 p: [
 "Companies are subject to statutory audit requirements under the Companies Act, subject to applicable provisions. The company therefore needs to maintain appropriate books and supporting documents.",
 "An auditor's appointment does not transfer the directors' responsibility for the company's affairs. The company and its responsible persons must continue to ensure that applicable legal and financial requirements are addressed.",
 ],
 },
 {
 h: "19. 'No business' does not necessarily mean 'no compliance'",
 p: [
 "This is perhaps one of the most important points for a new entrepreneur.",
 "Suppose you register a Private Limited Company today and do not conduct any business for the next twelve months. It does not automatically mean:",
 { quote: "No turnover = No compliance" },
 "The company continues to exist as a legal entity until it is legally closed, struck off or otherwise ceases in accordance with applicable law. Certain statutory and tax-related obligations may therefore continue.",
 "Before incorporating, ask yourself:",
 { quote: "If my business does not start for one year, am I prepared to maintain the company?" },
 ],
 },
 {
 h: "20. Closing the company is also a process",
 p: [
 "Sometimes entrepreneurs think:",
 { quote: "If the business does not work, I will simply stop using the company." },
 "That is not necessarily sufficient. Depending upon the circumstances, a company may need to follow the applicable legal process for closure, strike-off or other form of cessation.",
 "Therefore, before starting: understand the beginning, understand the ongoing responsibilities, and understand the possible exit.",
 ],
 },
 {
 h: "21. Ask what happens AFTER registration",
 p: [
 "Before paying anyone for company incorporation, ask:",
 { h3: "Before incorporation" },
 {
 list: [
 "Is Private Limited the appropriate structure?",
 "Who should be the shareholders?",
 "Who should be the directors?",
 "What should the shareholding be?",
 "What business activities will be undertaken?",
 "What registrations may be required?",
 ],
 },
 { h3: "During incorporation" },
 {
 list: [
 "What documents are required?",
 "What government fees apply?",
 "What professional fees apply?",
 "What is included in the quoted price?",
 "What will be the final structure?",
 ],
 },
 { h3: "After incorporation" },
 {
 list: [
 "What happens to the bank account?",
 "What about share capital?",
 "What about INC-20A, where applicable?",
 "What about accounting?",
 "What about GST?",
 "What about Income Tax?",
 "What about TDS?",
 "What about Professional Tax?",
 "What about PF and ESI if employees are hired?",
 "What about annual MCA compliance?",
 "Who will maintain the company's records?",
 ],
 },
 "These questions are more important than simply asking:",
 { quote: "How much is Pvt Ltd registration?" },
 ],
 },
 {
 h: "22. ₹999 or ₹9,999 is a price — not a business decision",
 p: [
 "A Private Limited Company should not be incorporated simply because registration is available at ₹999.",
 "It should not be incorporated simply because another person says:",
 { quote: "Everyone is doing Pvt Ltd." },
 "It should not be incorporated simply because someone says:",
 { quote: "You need a company to look professional." },
 "And it should not be incorporated simply because:",
 { quote: "The offer expires today." },
 "The decision should come from understanding the business requirement.",
 ],
 },
 {
 h: "23. Think about the next 3–5 years",
 p: [
 "Before incorporating, ask: where do I expect this business to be in three years? Will there be:",
 {
 list: [
 "Multiple founders?",
 "Employees?",
 "Investors?",
 "GST?",
 "Significant turnover?",
 "Interstate business?",
 "Foreign customers?",
 "Import / export?",
 "Loans?",
 "Intellectual property?",
 "Multiple locations?",
 "New shareholders?",
 ],
 },
 "The structure chosen today can have implications for the future.",
 ],
 },
 {
 h: "24. A Private Limited Company is a responsibility",
 p: [
 "A company is not just: Certificate of Incorporation + PAN + TAN + Bank Account.",
 "It is an ongoing legal structure. It involves:",
 {
 list: [
 "Company",
 "Directors",
 "Shareholders",
 "Accounts",
 "Tax",
 "Employees",
 "Contracts",
 "Government filings",
 "Records",
 "Compliance",
 ],
 },
 "The entrepreneur should understand this before incorporation.",
 ],
 },
 {
 h: "25. The right question to ask",
 p: [
 "Instead of asking:",
 { quote: "How cheaply can I register my Pvt Ltd Company?" },
 "ask:",
 { quote: "Why should I register a Pvt Ltd Company, what responsibilities will it create, and am I prepared to maintain it?" },
 "That is a much more important question.",
 ],
 },
 {
 h: "26. Think first. Register later. Build better.",
 p: [
 "At HG Corporates, the objective is to help entrepreneurs understand company registration before they make the decision to register.",
 "A founder should understand:",
 {
 list: [
 "WHY a company is required.",
 "WHICH structure is appropriate.",
 "WHO should be the shareholders.",
 "WHO should be the directors.",
 "WHAT laws may apply.",
 "WHAT compliance will continue.",
 "WHAT it may cost to maintain the company.",
 "WHAT responsibilities arise after incorporation.",
 "WHAT happens if the business succeeds — and what happens if it does not.",
 ],
 },
 "Do not register a Pvt Ltd merely because someone is offering it for ₹999 or ₹9,999.",
 { quote: "Understand first. Decide second. Register third." },
 { quote: "Think first. Register later. Build better." },
 ],
 },
 ],
 },
];

export const allResources = [featured, ...posts];

export const getResourceBySlug = (slug) =>
 allResources.find((r) => r.slug === slug) || null;
